/**
 * Subscription helpers — centralize the logic around free trials and
 * effective plan so callers don't have to re-derive it everywhere.
 *
 * The 7-day Ultra trial writes `plan: 'ultra'` + `trialEndsAt` to the user doc.
 * Until `trialEndsAt` passes, they get full Ultra access. After it passes,
 * `getEffectivePlan()` collapses them back to 'free' until they check out
 * via Stripe (which clears the trial flags and sets a real subscription).
 *
 * `hasUsedTrial` is set to true at trial start and NEVER cleared — this is
 * the anti-reuse flag. Combined with gating data exports during trial, it
 * prevents the "start trial, export, signup with new email, import" loop.
 */

import type { UserProfile, PlanType } from '../types';
import { PLAN_LIMITS } from '../types';

/**
 * A profile, as far as the subscription helpers are concerned.
 *
 * `subscription` is OPTIONAL because a free teacher genuinely has no subscription
 * object. Requiring one only ever type-checked because callers happened to pass whole
 * profiles; the moment a caller assembled a partial, it was a lie.
 */
type SubProfile = Partial<Pick<UserProfile, 'subscription'>> | null | undefined;

/**
 * AI credits the TRIAL includes for the WHOLE trial — a lifetime cap, not monthly.
 *
 * RAISED FROM 15 TO 40, as a forced consequence of free going to 15 a month.
 * At 15 the trial granted exactly what a free account already gets every month,
 * so it was not an upgrade in any sense: the credit gate checks the trial FIRST
 * on the stated grounds that it is the larger allowance, and the paywall offered
 * "start a free trial for 15 more" to someone who had 15 refreshing anyway. A
 * test pins FREE_AI_CREDITS < TRIAL_AI_CREDITS precisely so this cannot pass
 * unnoticed, and it did not.
 *
 * 40 over seven days is enough to write a real reporting run — about thirteen
 * six-to-eight-sentence secondary comments at 3 credits each, or ten elementary
 * ones at 4 — which is the thing the trial exists to let someone feel.
 *
 * The exposure is bounded and small: no credit card is required, so worst case
 * is 40 generations at roughly $0.017 each, about $0.68 per fraudulent account,
 * and evaluateTrialGate already refuses an unverified email.
 */
export const TRIAL_AI_CREDITS = 40;

/**
 * The free plan's MONTHLY AI allowance, DERIVED from PLAN_LIMITS.
 *
 * It was a literal, mirrored by hand in netlify/functions/shared/aiCredits.ts with
 * a test pinning the two equal. Both are now read from the one plan table, which
 * is the only way the pricing surfaces and the server's enforcement cannot
 * disagree — and they did disagree in a way no test could see: two hand-written
 * copies of the monthly caps lived on the server and were not connected to
 * PLAN_LIMITS at all.
 *
 * Kept as a named export because the paywall and the plan cards read it, and
 * because `FREE_AI_CREDITS < TRIAL_AI_CREDITS` is an invariant a test pins: the
 * credit gate checks the trial first on the stated grounds that it is the larger
 * allowance, so raising free past the trial silently inverts that ordering and
 * makes "start a free trial for 15 more" a worse offer than staying put.
 */
export const FREE_AI_CREDITS = PLAN_LIMITS.free.aiGenerations;

/**
 * True if the user is currently in a valid (not-yet-expired) free trial.
 *
 * `now` is injectable so callers that already take an explicit instant can stay
 * deterministic all the way down. A function that accepts a `now` and then
 * consults the wall clock underneath it is only half-testable, and the tests it
 * carries pass until the date rolls past their fixture.
 */
export const isOnTrial = (user: SubProfile, now: Date = new Date()): boolean => {
  const sub = user?.subscription;
  if (!sub?.trialEndsAt) return false;
  return new Date(sub.trialEndsAt).getTime() > now.getTime();
};

/**
 * May this user run BULK AI generation — the batch endpoints rather than one
 * generation at a time?
 *
 * Mirrors `ALLOWED_PLANS` in netlify/functions/ai-batch-create.ts and
 * ai-generate-variants.ts, which is the gate that actually enforces it. A test
 * pins the two together, because the failure is a 403 with no paywall: the
 * button is enabled, the teacher presses it, and the server refuses in language
 * meant for a developer.
 *
 * That was briefly live. Unlocking `hasReports` on free put the Reports view —
 * and its "generate all" button — in front of free teachers for the first time.
 * The existing guard there is a CREDIT check, which catches a 30-student class
 * (90 credits against 15) but passes a five-student one, and then the server
 * refused it.
 *
 * Bulk is the right thing to charge for: it is the difference between a feature
 * a teacher can evaluate and a feature that makes a whole class tractable, and
 * it is throughput rather than capability, so nobody is locked out of the
 * product itself.
 */
export const hasBulkAi = (user: SubProfile & { createdAt?: string } | null | undefined): boolean =>
  getEffectivePlan(user) !== 'free';

/** True if the user has ever started a trial. Blocks re-triggering. */
export const hasUsedTrial = (user: SubProfile): boolean => !!user?.subscription?.hasUsedTrial;

/**
 * The effective plan to use for feature gating. Collapses an expired trial
 * back to 'free'. All UI and permission checks should use this rather than
 * reading `subscription.plan` directly.
 */
export const getEffectivePlan = (user: SubProfile, now: Date = new Date()): PlanType => {
  const sub = user?.subscription;
  if (!sub) return 'free';
  // Stripe converted them — trust the real plan
  if (sub.stripeSubscriptionId) return sub.plan;
  /**
   * An enterprise SEAT is granted server-side, not bought through Stripe, so it
   * has no stripeSubscriptionId to vouch for it — and provisioning does not
   * clear `trialEndsAt`/`hasUsedTrial`.
   *
   * Without this line, the ordinary onboarding order (teacher signs up, gets the
   * trial, THEN their school grants them a seat) collapsed them to 'free' on the
   * day that stale trial date passed: enterprise features gone while the seat,
   * and the school's bill, carried on. Safe to key on because every
   * `subscription.*` field is server-only and pinned immutable in
   * firestore.rules.
   *
   * `enterpriseDomain`, not `plan`, is what makes it safe: BOTH revocation paths
   * delete the domain, so a pulled seat can never satisfy this. Do not relax it
   * to a bare plan check.
   *
   * KNOWN, SEPARATE, NOT FIXED HERE: a removed teacher who pays for their own
   * plan can still come out of revocation holding `plan: 'enterprise'` with a
   * live stripeSubscriptionId, which the FIRST branch above then honours.
   * enterprise-provision.ts overwrites `subscription.plan` without recording
   * what it was, so neither revocation path has a tier to restore — which is
   * also why the two paths disagree (remove-school-member.ts keeps the stale
   * 'enterprise'; enterprise-admin's removeSeat forces 'free' over a plan the
   * teacher is still being billed for). Fixing it means stashing the prior plan
   * at provision time and restoring it on release, plus a decision about
   * teachers already seated under the old behaviour.
   *
   * Left as-is DELIBERATELY (Reece, 2026-08-24): effectively no teacher holds a
   * personal Stripe subscription AND an enterprise seat, and that combination is
   * the only trigger. For everyone who actually exists both paths already write
   * 'free', which is correct. Recorded rather than fixed because the day someone
   * does buy a personal plan and then get seated, this is live — and Stripe still
   * knows the tier Firestore forgot, so it self-heals on their next subscription
   * webhook rather than persisting forever.
   */
  if (sub.plan === 'enterprise' && sub.enterpriseDomain) return 'enterprise';
  // Trial still active → whatever plan is set (currently 'ultra')
  if (isOnTrial(user, now)) return sub.plan;
  // Trial was the only reason they had elevated access and it's over
  if (sub.hasUsedTrial && !sub.stripeSubscriptionId) return 'free';
  // No trial at all — just their plan
  return sub.plan;
};

/**
 * Profile shape the storage helpers need — plan (for the cap), usage counter, and
 * `createdAt`, because the free storage allowance now depends on when the account was
 * created. Optional so a partial profile still type-checks; see `planLimitsFor` for
 * which way a missing value falls.
 */
type StorageProfile = (Partial<SubProfile> & { storageBytesUsed?: number; createdAt?: string }) | null | undefined;

/**
 * The teacher's total upload allowance in bytes, from their effective plan's
 * storageMB. Returns Infinity when a tier is unlimited (non-finite storageMB),
 * so callers can skip the aggregate cap check.
 */
export const storageCapBytes = (user: StorageProfile): number => {
  const mb = planLimitsFor(user).storageMB;
  return Number.isFinite(mb) ? mb * 1024 * 1024 : Infinity;
};

/** Bytes the teacher has left before hitting their cap. Infinity if uncapped. */
export const storageRemainingBytes = (user: StorageProfile): number => {
  const cap = storageCapBytes(user);
  if (!Number.isFinite(cap)) return Infinity;
  return Math.max(0, cap - (user?.storageBytesUsed ?? 0));
};

/** Days remaining in the current trial (0 if not on trial). */
export const getTrialDaysRemaining = (user: SubProfile): number => {
  const sub = user?.subscription;
  if (!sub?.trialEndsAt) return 0;
  const msLeft = new Date(sub.trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
};

/** Plan ranking for "at least" feature gates. Enterprise sits above Ultra. */
const PLAN_RANK: Record<PlanType, number> = {
  free: 0,
  basic: 1,
  pro: 2,
  ultra: 3,
  enterprise: 4,
};

/** True if the user's effective plan is at least `min` (by rank). */
export const planAtLeast = (user: SubProfile, min: PlanType): boolean =>
  PLAN_RANK[getEffectivePlan(user)] >= PLAN_RANK[min];

/**
 * Whether the user has Ultra-tier features. Enterprise inherits everything
 * Ultra has, so feature gates should use this rather than `=== 'ultra'`
 * (which would wrongly lock enterprise users out).
 */
export const hasUltraFeatures = (user: SubProfile): boolean => planAtLeast(user, 'ultra');

/**
 * Google Classroom import and sync. Pro and up.
 *
 * It sat on Ultra, which cost us more than it protected. It carries no
 * marginal cost — the Google Classroom API is free and the functions behind it
 * spend nothing on AI — so the gate was pure differentiation. And it was
 * differentiating in the wrong direction: it is the fastest route from signup
 * to a populated gradebook, which is precisely what a teacher deciding whether
 * to pay needs to see working. Worse, once the trial started granting Ultra, a
 * teacher who imported their real classes during the trial and then converted
 * to Pro — the tier we recommend — would have LOST the feature that sold them.
 *
 * Ultra still differentiates on the things that do cost money or scale: 50
 * classes, 75 students each, 300 AI credits, unlimited mark sets.
 *
 * Named rather than reusing hasUltraFeatures so moving one feature between
 * tiers never silently moves another. Reads the PLAN_LIMITS flag rather than a
 * rank comparison, so the gate, the signup cards and the pricing copy all move
 * together when the tier changes — they are all derived from this one flag.
 */
export const hasClassroomImport = (user: SubProfile): boolean =>
  PLAN_LIMITS[getEffectivePlan(user)]?.hasClassroomImport === true;

/**
 * Whether the user can use AI grading. Available on Pro and up (Pro, Ultra,
 * Enterprise) — each metered against their own monthly AI-credit budget
 * (Pro 150, Ultra 300, Enterprise uncapped). Never Free/Basic.
 */
export const hasAiGrading = (user: SubProfile): boolean => planAtLeast(user, 'pro');

/** True if the user is on the hidden enterprise plan. */
export const isEnterprise = (user: SubProfile): boolean => getEffectivePlan(user) === 'enterprise';

/** Days of grace after a paid subscription lapses before extra classes lock. */
export const LAPSE_GRACE_DAYS = 3;

export interface LapseState {
  /** A paid subscription ended and hasn't been renewed. */
  lapsed: boolean;
  /**
   * WHY they lapsed. 'trial' needs different words from 'lapse' — nobody's
   * payment failed, their free week simply ran out — and the modal keys off it.
   */
  reason: 'lapse' | 'trial' | null;
  /** Still inside the grace window — everything works, warn to renew. */
  inGrace: boolean;
  /** Grace is over — all active classes but one are locked. */
  locked: boolean;
  graceEndsAt: string | null;
  daysLeft: number;
}

/**
 * Subscription-lapse state. When a PAID subscription expires the webhook drops
 * the user to free and stamps lapsedAt, starting a 3-day grace. During grace
 * everything works (with a warning); after it, all active classes but one lock
 * until renewal. An expired TRIAL is not a lapse (no lapsedAt) — those just
 * become free normally.
 */
export const getLapseState = (
  user: SubProfile,
  opts: { isEnterpriseTeacher?: boolean } = {},
): LapseState => {
  const none: LapseState = { lapsed: false, reason: null, inGrace: false, locked: false, graceEndsAt: null, daysLeft: 0 };
  const sub = user?.subscription;
  if (getEffectivePlan(user) !== 'free') return none; // paying (or renewed) — nothing to lapse
  /**
   * A teacher whose SCHOOL or BOARD pays.
   *
   * Their immunity is not on their own user doc: board checkout writes only
   * boards/{id}, and the seat shows up as a schoolAssociation whose school is
   * active (AuthContext derives `isEnterpriseTeacher` from it). Meanwhile every
   * teacher signup auto-starts the 7-day Ultra trial, so that teacher carries an
   * expired trial for life and getEffectivePlan reads 'free'.
   *
   * Without this, a board-paid teacher would have had their school's own
   * timetabled classes locked, under a banner telling them to renew — while the
   * upgrade modal told them their school's plan already covers everything.
   */
  if (opts.isEnterpriseTeacher) return none;

  /**
   * An expired TRIAL now lapses too.
   *
   * It used to lock nothing: `lapsedAt` is stamped only by the Stripe webhook
   * when a PAID subscription ends, so a teacher could take the 7-day Ultra
   * trial, build five classes, and keep all five open on the free tier for
   * ever. The free cap only blocked CREATING a sixth, so the trial ending was
   * something nobody could feel.
   */
  const lapseFrom = sub?.lapsedAt
    ? new Date(sub.lapsedAt).getTime()
    : trialLapseStart(sub);
  if (!lapseFrom) return none;

  const graceEnd = lapseFrom + LAPSE_GRACE_DAYS * 86_400_000;
  const now = Date.now();
  const inGrace = now < graceEnd;
  return {
    lapsed: true,
    reason: sub?.lapsedAt ? 'lapse' : 'trial',
    inGrace,
    locked: !inGrace,
    graceEndsAt: new Date(graceEnd).toISOString(),
    daysLeft: Math.max(0, Math.ceil((graceEnd - now) / 86_400_000)),
  };
};

/**
 * When an expired trial's grace begins — never before TRIAL_LOCK_LAUNCH.
 *
 * Teachers whose trial ended weeks ago must not be locked out the instant this
 * ships. The floor gives every one of them the same full grace window starting
 * at launch, so nobody loses a class without warning in the middle of a term.
 * (Same shape as FEATURE_GATE_LAUNCH, which exists for the same reason.)
 */
const trialLapseStart = (sub: NonNullable<SubProfile>['subscription'] | undefined): number | null => {
  const endsAt = sub?.trialEndsAt ? new Date(sub.trialEndsAt).getTime() : NaN;
  if (!Number.isFinite(endsAt) || endsAt > Date.now()) return null; // never trialed, or still running
  return Math.max(endsAt, new Date(TRIAL_LOCK_LAUNCH).getTime());
};

/**
 * Expired trials do not lock before this instant. Set far enough ahead that
 * every teacher already past their trial gets notice rather than a locked
 * gradebook mid-week.
 */
export const TRIAL_LOCK_LAUNCH = '2026-09-19T00:00:00.000Z';

/**
 * Class ids locked by a lapse. Once grace is over, every active (non-archived)
 * class but the most recently created one — the single free allowance — is
 * locked: it can't be opened, but it CAN still be archived/deleted so the owner
 * can manage their own data. Empty while in grace or not lapsed.
 */
export const lockedClassIds = (
  classes: Array<{ id: string; isArchived?: boolean; createdAt?: string; isTemplate?: boolean; schoolId?: string | null; ownerId?: string }>,
  lapse: LapseState,
  opts: { uid?: string } = {},
): Set<string> => {
  if (!lapse.locked) return new Set<string>();
  const active = classes.filter(c => {
    if (c.isArchived) return false;
    /**
     * A TEMPLATE is not a class you teach — the dashboard counts it separately
     * and free allows five. Counting them here let a freshly saved template
     * become the "most recent" survivor and lock the teacher's only real class.
     */
    if (c.isTemplate) return false;
    /**
     * A SCHOOL class belongs to the institution that paid for it, and may have
     * been created by the timetable publisher rather than by this teacher. A
     * personal plan lapsing must not lock it.
     */
    if (c.schoolId) return false;
    /**
     * Only classes this teacher OWNS. getTeacherClasses merges in everything
     * they co-teach, so without this a teacher could have a colleague's classes
     * locked by their own lapse — and a co-teacher has no Archive action on
     * someone else's class, so no way to reach the work at all.
     */
    if (opts.uid && c.ownerId && c.ownerId !== opts.uid) return false;
    return true;
  });
  if (active.length <= 1) return new Set<string>();
  const kept = [...active].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
  return new Set(active.filter(c => c.id !== kept.id).map(c => c.id));
};

/** Plan-gated boolean features (Attendance/Reports/Seating/Gamification are
 *  Pro+ per PLAN_LIMITS). */
export type GatedFeature = 'hasAttendance' | 'hasReports' | 'hasSeating' | 'hasGamification';

/**
 * Accounts created BEFORE this instant are grandfathered — they keep every
 * feature they had before plan-gating shipped, so no existing user loses access.
 * New signups from here on are gated by plan. (Set to just after gating launch.)
 */
export const FEATURE_GATE_LAUNCH = '2026-07-19T00:00:00.000Z';

/**
 * Does this roster row consume a seat against the teacher's plan?
 *
 * Children do. A colleague taking the class — professional development, a pilot, a course
 * they are enrolled in — does not, and `join-class-participant` already said so before
 * the cap reached the join paths: "Real students are counted separately by the owner's
 * own plan limit." Counting them broke that, and two staff in a free class left room for
 * only eight children.
 *
 * Withdrawn rows do not count either. They left; charging a teacher for children who
 * went in September would make the cap mean something nobody agreed to.
 *
 * Exported from here rather than the functions folder because BOTH sides must agree: a
 * client that counts differently from the server shows a teacher a full class the server
 * will happily add to, or the reverse.
 */
export const countsTowardRosterCap = (row: unknown): boolean => {
  const r = row as { status?: unknown; isTeacherParticipant?: unknown; isStaffParticipant?: unknown } | null;
  if (!r) return false;
  if (r.status === 'withdrawn') return false;
  if (r.isTeacherParticipant === true || r.isStaffParticipant === true) return false;
  return true;
};

/**
 * Accounts created BEFORE this instant keep the OLD, roomier free-plan limits.
 *
 * The free tier tightened for new signups (25 students -> 15, 5 templates -> 2, 50MB
 * -> 25MB) to make the first paid step worth taking. Nobody who already signed up is
 * touched: this is a date comparison against `createdAt`, so there is no migration, no
 * write to a single existing user document, and nothing to get wrong halfway through.
 *
 * It is deliberately a SECOND constant rather than reusing FEATURE_GATE_LAUNCH. That
 * one is 2026-07-19, so reusing it would have re-tightened everyone who signed up in
 * the months since — the exact group most likely to notice and least likely to forgive
 * it. Cohorts, oldest first:
 *
 *   created < FEATURE_GATE_LAUNCH  — unlimited students, every feature. Untouched.
 *   created < FREE_SQUEEZE_LAUNCH  — live student cap / 5 templates / 50MB.
 *   created >= FREE_SQUEEZE_LAUNCH — the new PLAN_LIMITS.free.
 *
 * Set just ahead of the deploy, so a teacher signing up while it ships lands in the
 * older, roomier cohort rather than the newer one. The boundary should err toward the
 * person, not toward us.
 */
export const FREE_SQUEEZE_LAUNCH = '2026-10-01T00:00:00.000Z';

/**
 * The free-plan values an account created before the squeeze keeps.
 *
 * Written out rather than derived from git history, because the point of a
 * grandfathered limit is that it stops moving when the current one does. Only the
 * fields that actually changed are listed; everything else comes from PLAN_LIMITS.free
 * so a future change to, say, the archive cooldown reaches every cohort.
 */
export const LEGACY_FREE_LIMITS = {
  // Seats inherit the live 30-student cap: retaining 25 would penalize older accounts.
  templates: 5,
  storageMB: 50,
} as const;

/**
 * The plan limits that apply to THIS user, cohort included.
 *
 * Every numeric free-tier limit must be read through here rather than from
 * `PLAN_LIMITS[plan]` directly. One missed reader is an existing teacher told their
 * class is full at fifteen when they have twenty-two in it — which is both a support
 * ticket and a broken promise, and it fails in the direction that costs trust.
 */
export const planLimitsFor = (
  user: (Partial<Pick<UserProfile, 'subscription'>> & { createdAt?: string }) | null | undefined,
  now: Date = new Date(),
): typeof PLAN_LIMITS[PlanType] => {
  const plan = getEffectivePlan(user, now);
  const base = PLAN_LIMITS[plan];
  // Only the free tier moved. A paid plan's limits are the same for everyone.
  if (plan !== 'free') return base;
  /**
   * An unknown creation date falls to the ROOMIER limits.
   *
   * Every stored profile carries `createdAt`, so this is about partial objects and
   * anything that fails to load one. The two ways to be wrong are not symmetric:
   * guessing "new" squeezes a teacher who has had twenty-two students in a class since
   * last term, which is a broken promise; guessing "old" gives a new signup ten
   * students more than intended, which is a little revenue. Err toward the person.
   */
  if (!user?.createdAt || user.createdAt < FREE_SQUEEZE_LAUNCH) {
    return { ...base, ...LEGACY_FREE_LIMITS };
  }
  return base;
};

/**
 * What the FREE tier would give this user, whatever plan they are on now.
 *
 * Distinct from `planLimitsFor`, which answers "what are this user's limits" — during a
 * trial that is Ultra's, because the effective plan is Ultra. The question a trial nudge
 * and a trial-ended paywall are actually asking is "what will they drop TO", and reading
 * the effective plan answered a different one: "Students per class: 75 → 75".
 *
 * Cohort-aware for the same reason as everything else here: a teacher who signed up
 * before the squeeze keeps their roomier template and storage limits.
 */
export const freeTierLimitsFor = (
  user: (Partial<Pick<UserProfile, 'subscription' | 'createdAt'>>) | null | undefined,
): typeof PLAN_LIMITS['free'] => {
  if (!user?.createdAt || user.createdAt < FREE_SQUEEZE_LAUNCH) {
    return { ...PLAN_LIMITS.free, ...LEGACY_FREE_LIMITS };
  }
  return PLAN_LIMITS.free;
};

/**
 * Whether the user may use a plan-gated feature. Enterprise + grandfathered
 * users always may; otherwise it's the PLAN_LIMITS flag for their effective plan.
 * Soft gate: callers show an upgrade prompt in place of the feature, they don't
 * destroy data.
 */
export const canUseFeature = (
  user: (Partial<Pick<UserProfile, 'subscription' | 'createdAt'>>) | null | undefined,
  feature: GatedFeature,
  opts?: { isEnterpriseTeacher?: boolean },
): boolean => {
  if (!user) return false;
  if (opts?.isEnterpriseTeacher) return true;
  if (isEnterprise(user)) return true;
  // Grandfather existing accounts.
  if (user.createdAt && user.createdAt < FEATURE_GATE_LAUNCH) return true;
  return PLAN_LIMITS[getEffectivePlan(user)][feature] === true;
};

/**
 * The students-per-class cap for this user. Grandfathered + enterprise users have
 * no cap (Infinity) so no existing roster is ever blocked; everyone else gets the
 * plan-matrix value. Soft: callers show an upgrade prompt at the cap.
 */
export const studentsPerClassCap = (
  user: (Partial<Pick<UserProfile, 'subscription' | 'createdAt'>>) | null | undefined,
  opts?: { isEnterpriseTeacher?: boolean },
): number => {
  if (!user) return PLAN_LIMITS.free.studentsPerClass;
  if (opts?.isEnterpriseTeacher || isEnterprise(user)) return Infinity;
  if (user.createdAt && user.createdAt < FEATURE_GATE_LAUNCH) return Infinity;
  return planLimitsFor(user).studentsPerClass;
};

/**
 * Platform owner emails — gate the enterprise admin panel UI. This only controls
 * VISIBILITY; the real authorization is enforced server-side against a verified
 * token email in enterprise-admin.ts. Keep in sync with PLATFORM_OWNER_EMAILS there.
 */
export const OWNER_EMAILS = ['reececantelon@gmail.com', 'reece@levelupclassroom.com'];

/** True if this profile's email is a platform-owner email (UI gate only). */
export const isPlatformOwner = (
  user: (SubProfile & { email?: string }) | { email?: string } | null | undefined,
): boolean => {
  const email = (user as { email?: string } | null | undefined)?.email;
  return !!email && OWNER_EMAILS.includes(email.toLowerCase());
};

/**
 * Whether the user is allowed to export their data.
 * False during an active trial — prevents the trial-export-rejoin abuse loop.
 * All export/print utilities should check this before running.
 */
export const canExportData = (user: SubProfile): boolean => !isOnTrial(user);

/** Reason string when export is blocked — useful for toast messages. */
export const EXPORT_BLOCKED_REASON =
  'Data export is disabled during your free Ultra trial. Upgrade to a paid plan to export.';

/**
 * Guard an export action. Returns true if the user can proceed; returns false
 * and shows an error toast if they can't (trial in progress). Callsite should
 * early-return on false.
 *
 * Usage:
 *   if (!checkExportAllowed(userProfile)) return;
 *   exportCSV(...);
 */
export const checkExportAllowed = (user: SubProfile): boolean => {
  if (canExportData(user)) return true;
  // Lazy-import to avoid a circular dep if notify ever reaches back here
  import('./notify').then(m => m.notifyError(EXPORT_BLOCKED_REASON, { duration: 6000 }));
  return false;
};
